import React, { useContext } from "react";

import { toast } from "react-toastify";

import { AppContext } from "../../../../context/app";
import { defaultToastOptions, extractError } from "../../../../utils/misc";
import {
  getWeekDay,
  removeTimezoneFromDateString,
} from "../../../../utils/dateUtils";
import {
  useCancelCalendarItemMutation,
  useDeleteClassTemplateMutation,
  useGetCalendarItemForEditQuery,
} from "../CalendarForm-generated.hooks";
import CalendarItemFormConfirmationModal, {
  CalendarItemConfirmationScope,
} from "../ConfirmationModal";
import { format } from "date-fns";
import { DialogModal } from "@pushpress/shared-components";
import { abbreviateDays } from "../CalendarForm.utils";

interface CancelCalendarItemProps {
  visible: boolean;
  uuid?: string;
  title?: string;
  onClose?: VoidFunction;
  onCalendarItemCancelled?: VoidFunction;
  onSavingStatus?: (saving: boolean) => void;
}

const CancelCalendarItem = ({
  visible,
  uuid,
  onClose,
  onCalendarItemCancelled,
  onSavingStatus,
}: CancelCalendarItemProps) => {
  const { client } = useContext(AppContext);

  const [cancelCalendarItemMutation] = useCancelCalendarItemMutation();
  const [deleteCalendarTemplate] = useDeleteClassTemplateMutation();

  const { data, loading } = useGetCalendarItemForEditQuery({
    variables: {
      getCalendarItemInput: {
        uuid: uuid || "",
        clientUuid: client.client_uuid || client.uuid,
      },
    },
    skip: !uuid,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
  });

  if (loading || !uuid) return null;

  const startDate = format(
    removeTimezoneFromDateString(data?.getCalendarItem?.startDatetime),
    "yyyy-MM-dd",
  );
  const templateId = data?.getCalendarItem.templateId;

  const onScopeSelected = async (saveScope: CalendarItemConfirmationScope) => {
    try {
      onSavingStatus?.(true);
      switch (saveScope) {
        case "this":
          await cancelCalendarItemMutation({
            variables: {
              cancelCalendarItemInput: {
                uuid: uuid,
              },
            },
          });
          break;
        case "all":
          await deleteCalendarTemplate({
            variables: {
              deleteCalendarTemplateItemInput: {
                series: templateId!,
                startDate,
                days: undefined,
              },
            },
          });
          break;
        case "all of weekday":
          await deleteCalendarTemplate({
            variables: {
              deleteCalendarTemplateItemInput: {
                series: templateId!,
                startDate,
                days: abbreviateDays([getWeekDay(startDate)]),
              },
            },
          });
          break;
        default:
          onSavingStatus?.(false);
          return;
      }
      toast.success(
        `Success! Your ${data?.getCalendarItem.type.toLowerCase()} has been deleted!`,
        defaultToastOptions,
      );
      onCalendarItemCancelled?.();
    } catch (err) {
      toast.error(extractError(err), defaultToastOptions);
    } finally {
      onSavingStatus?.(false);
      onClose?.();
    }
  };

  const isAttachedToATemplate = !(Number(templateId) === 0);

  return (
    <>
      {isAttachedToATemplate ? (
        <CalendarItemFormConfirmationModal
          classItem={data?.getCalendarItem}
          title={`Delete ${data?.getCalendarItem.type.toLowerCase()}`}
          visible={isAttachedToATemplate && visible}
          onClose={onClose}
          onScopeSelected={onScopeSelected}
        />
      ) : (
        <DialogModal
          visible={!isAttachedToATemplate && visible}
          closeModal={onClose}
          primaryHandler={() => onScopeSelected("this")}
          primaryBtnTitle="Delete"
          title={`Are you sure you want to delete this ${data?.getCalendarItem.type.toLowerCase()}?`}
          secondaryBtnTitle="Go Back"
          secondaryHandler={onClose}
        />
      )}
    </>
  );
};

export default CancelCalendarItem;
